import {mapGetters} from "vuex";
import debounce from "lodash/debounce"

import mixin_products from "@/mixins/products";
import mixin_address from "@/mixins/address";
import {ORDER_STATUS} from "@/constants";

export default {
    mixins: [mixin_address, mixin_products],
    data() {

        return {
            widget_loaded: false,
            widget_listener: null,
            widgetListenerInterval: null,
            widget_flowParameter: "wgtflow",
            widget_flows: ["delivery_issue"]
        }
    },
    computed: {
        ...mapGetters({
            widget_orders: "user/orders/items",
            widget_options: "widget/options"
        }),
        widget_isLoaded() {

            return !!window.__SupportWidgetListener;
        },
        widget_optionsJson() {

            return JSON.stringify({
                anchorMargin: "60px 15px 0 0",
                token: this.$store.getters["user/token"]
            });
        },
        widget_optionsFlowJson() {

            const flow = this.$route.query[this.widget_flowParameter];

            if (flow && this.widget_flows.includes(flow)) {

                return JSON.stringify({
                    anchorMargin: "60px 15px 0 0",
                    token: this.$store.getters["user/token"],
                    selection: this.widget_getFlowSelection(flow)
                });
            }

            return null;
        },
        widget_optionsJson_productIssues_productDamaged() {

            return JSON.stringify({
                anchorMargin: "60px 15px 0 0",
                token: this.$store.getters["user/token"],
                selection: [{
                    component: "category",
                    value: "Product Issues"
                }, {
                    component: "issue",
                    value: "Product damaged"
                }]
            });
        },
        widget_optionsJson_orderQuestions_whereOrder() {

            return JSON.stringify({
                anchorMargin: "60px 15px 0 0",
                token: this.$store.getters["user/token"],
                selection: [{
                    component: "category",
                    value: "Order Questions"
                }, {
                    component: "issue",
                    value: "Where is my order?"
                }]
            });
        },
        widget_optionsModels() {

            return {
                widgetOptions: this.widget_optionsJson
            }
        },
        widget_displayWidgetButton() {

            return this.$store.getters["widget/display"]
                && -1 === ["helpdesk"].indexOf(this.$route.name)
                && null === this.$store.getters["user/impersonatorToken"]
                && this.$store.getters["user/isAuthenticated"];
        }
    },
    methods: {
        widget_load() {

            if (!this.widget_loaded && process.env.VUE_APP_WIDGET_ENABLED && process.env.VUE_APP_WIDGET_URL) {
                this.widget_loaded = true;

                // Run widget before a script loaded to be sure
                this.widget_refresh()

                const script = document.createElement('script')
                script.type = "text/javascript"
                script.src = process.env.VUE_APP_WIDGET_URL

                document.head.appendChild(script)
            }
        },
        widget_init() {

            const widgetOptions = this.widget_options;

            const token = widgetOptions.token ?? this.$store.getters["user/token"];
            const isOrganicToken = !!this.$store.getters["user/token"]

            const addresses = this.widget_options.addresses
                ? this.widget_options.addresses
                : (isOrganicToken ? this.$store.getters["user/address/items"] : []);
            const countries = this.$store.getters["db/countries/items"].reduce(
                (result, country) => ({...result, [country.alpha3]: country.name}),
                {}
            )
            const allAddresses = this.$store.getters["user/address/all"]
            const notVerifiedAddresses = addresses.filter(address => true === address.tfaNeedAddressVerification);
            const callbackAddressTFA = addressId => this.$store.dispatch("user/address/confirm", addressId);
            const products = (this.widget_options.orders ? this.widget_options.orders : (token ? this.widget_orders : []))
                .reduce((result, order) => {

                    const address = allAddresses.find(address => address._id === order.addressId)

                    return result.concat(
                        order.products.map(product => ({
                            ...product,
                            address: address ? [countries[address.country]].concat(this.address_getTextRepresentation(address)).join("\n") : ""
                        }))
                    );
                }, [])
                .filter(product => -1 === [ORDER_STATUS.CANCELED, ORDER_STATUS.RETURNED, ORDER_STATUS.TRANSFERRED].indexOf(product.status))
                .filter(product => {

                    if (this.widget_options.orderProduct) {

                        if (product.sku == this.widget_options.orderProduct.sku) {

                            return this.widget_options.orderProduct._id == product._id;
                        }
                    }

                    return true;
                })
                .reduce((result, product) => {

                    const index = result.findIndex(
                        addedProduct => addedProduct.sku === product.sku && product.orderId === addedProduct.orderId
                    );

                    if (-1 === index) {

                        const firstOrderStatus = product.statusHistory[product.statusHistory.length - 1];

                        result.push({
                            ...product,
                            _id: product._id,
                            name: this.products_translateProductName(product),
                            description: this.products_translateProductDescription(product),
                            note: this.products_translateProductNote(product),
                            quantity: product.quantity ? product.quantity : 1,
                            orderCreated: firstOrderStatus?.createdOn
                                ? this.$t('widget.ordered', {date: this.d(firstOrderStatus.createdOn, 'date')})
                                : '',
                            estimatedDeliveryDate: this.products_translateProductEstimatedShipDate(product),
                            statusesMap: {
                                [product._id]: product.status
                            }
                        });
                    } else {

                        result[index]._id += ',' + product._id; // combine multiple product item identifiers
                        result[index].quantity += product.quantity ? product.quantity : 1;
                        result[index].statusesMap[product._id] = product.status;
                    }

                    return result;
                }, []);

            window.__SupportWidgetListener({
                token,
                flow: widgetOptions.flow ?? null,
                addresses,
                not_verified_addresses: notVerifiedAddresses,
                callbackAddressTFA,
                products,
                email: widgetOptions.email ?? this.$store.getters["user/profile/email"],
                name: widgetOptions.name ?? this.$store.getters["user/profile/name"],
                country: widgetOptions.country ?? this.$store.getters["user/profile/country"],
                language: this.$store.getters["i18n/language"].value,
                target_user: this.widget_options.target_user,
                stylist_tags: widgetOptions.stylistTags ?? this.$store.getters["user/stylistTags"],
                constraints: widgetOptions.constraints ?? this.$store.getters["user/constraints/type"]
            });
        },
        widget_refresh() {

            if ("function" !== typeof window.__SupportWidgetListener && !this.widget_listener) {

                // Wait for widget callback is ready for passing data
                this.widget_listener = setInterval(() => {

                    if (window.__SupportWidgetListener) {

                        // Remove existing listener
                        clearInterval(this.widget_listener);
                        this.widget_init();
                    }
                }, 5);
            }
        },
        widget_refresh_delayed: debounce(function () {

            return this.widget_refresh();
        }, 500),
        widget_appendSelectionProduct(options, product) {

            const trigger = JSON.parse(options);

            trigger.selection.push({
                component: "productSingle",
                value: product._id
            });

            return JSON.stringify(trigger);
        },
        widget_appendSelectionProducts(options, products) {

            const trigger = JSON.parse(options);

            trigger.selection.push({
                component: "products",
                value: products.map(product => product._id)
            });

            return JSON.stringify(trigger);
        },
        widget_getFlowSelection(flow) {

            if ("delivery_issue" === flow) {

                return [{
                    component: "category",
                    value: "Order Questions"
                }, {
                    component: "issue",
                    value: "Issue with my delivery"
                }]
            }

            return undefined;
        }
    }
}